import { useContext } from "react";
import { useParams } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ChecklistQueryKeys } from "@/components/Procedures/API/ChecklistQueries";
import { CreateChecklistRequest } from "@/components/Procedures/Types/ManualChecklistTypes";

import { SATOSPortalNew } from "@/DataAdapter/Portal";
import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { Checklist } from "@/model/Checklist/Checklist";
import { ProcedureUpdateNoticeRequest } from "@/model/Competence/ProcedureUpdateNoticeRequest";
import { CompetenceStatusUpdate } from "@/model/Competence/UserCompetence";
import { CreateNewProcedureRequest } from "@/model/Procedure/CreateNewProcedure";
import { DocumentImportPlan } from "@/model/Procedure/DocumentImportPlan";
import { DocumentUpdateRequest } from "@/model/Procedure/DocumentUpdateRequest";
import { EditProcedure } from "@/model/Procedure/EditProcedure";
import { MeasurementPollerConfigurationEndpoint } from "@/model/Procedure/GetKSpiceSettings";
import {
	NodeDiagramTemplate,
	NodeDiagramTemplateReturn,
} from "@/model/Procedure/NodeDiagramTemplate";
import { ProcedureAccordionMove } from "@/model/Procedure/ProcedureAccordionMove";
import { ProcedureDetails } from "@/model/Procedure/ProcedureDetail";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";
import {
	CreateNewProcedureNodeRequest,
	DeleteProcedureNodeRequest,
	UploadProcedureNodeDocumentRequest,
} from "@/model/Procedure/ProcedureNodeDependency";
import { ProcedureNodeDiagramSave } from "@/model/Procedure/ProcedureNodeDiagramSave";
import {
	ProcedureNodeStepComment,
	SuggestionRequest,
} from "@/model/Procedure/ProcedureNodeStepComment";
import {
	ConvetToManualChecklistRequest,
	DeleteProcedureChecklistRequest,
	ProcedureNodeStepStatus,
	ProcedureNodeSteps,
	QueryUpsertProcedureNodeSteps,
} from "@/model/Procedure/ProcedureNodeSteps";
import { SaveProcedureRequest } from "@/model/Procedure/ProcedureSaveRequest";
import { UpdateProcedureDetailStepStatusRequest } from "@/model/Procedure/UpdateProcedureDetailStepStatusRequest";
import {
	ImportProcedureNodeDocumentRequest,
	RefreshSharePointDocumentRequest,
	UpdateProcedureNodeRequest,
} from "@/model/Procedure/UpdateProcedureNodeRequest";
import { UppdatedProcedureRequest } from "@/model/Procedure/UppdatedProcedureRequest";
import { UserContext } from "@/model/User/UserContext";

import {
	ProcedureQueryKeys,
	ReorderAccordion,
} from "@/service/ProcedureQueries";
import { TaskQueryKeys } from "@/service/TaskQueries";
import { RouteParams } from "@/service/Utilities";

// TODO: Check update render!!!
export const PostProcedureUpdateSuggestions = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: SuggestionRequest) =>
			SATOSPortal.PostProcedureUpdateSuggestion(request),
		onMutate: async (newSuggestion: SuggestionRequest) => {
			await queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					TaskQueryKeys.procedureUpdatesSuggestions,
				],
			});

			const previousSuggestions = queryClient.getQueryData<any[]>([
				TaskQueryKeys.procedureUpdatesSuggestions,
			]);

			// Optimistically add the new suggestion to the current list
			queryClient.setQueryData(
				[TaskQueryKeys.procedureUpdatesSuggestions],
				(old: any[] = []) => [...(old ?? []), newSuggestion],
			);

			return { previousSuggestions };
		},
		onError: (err, newSuggestion, context) => {
			// Rollback to previous state if an error occurs
			if (context?.previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					context.previousSuggestions,
				);
			}
		},
		onSettled: () => {
			// Ensure that queries are refetched after mutation
			queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
			// Clears the cache for the checklist page
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useDeleteProcedureComment = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (request: {
			commentId: number;
			checklistId: number;
			stepId: number;
		}) => SATOSPortal.DeleteProcedureComment(request.commentId),
		onMutate: async (request) => {
			await queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					TaskQueryKeys.procedureUpdatesSuggestions,
				],
			});

			const previousSuggestions = queryClient.getQueryData<any[]>([
				TaskQueryKeys.procedureUpdatesSuggestions,
			]);

			// Optimistically remove the suggestion from the current list
			queryClient.setQueryData(
				[TaskQueryKeys.procedureUpdatesSuggestions],
				(old: any[] = []) =>
					old?.filter((suggestion) => suggestion.id !== request.commentId) ??
					[],
			);

			const commentsCacheKey = [
				ChecklistQueryKeys.checklist,
				request.checklistId,
				ChecklistQueryKeys.comments,
			];
			await queryClient.cancelQueries({ queryKey: commentsCacheKey });

			const previousComments =
				queryClient.getQueryData<any[]>(commentsCacheKey);

			// Optimistically remove the suggestion from the current list
			queryClient.setQueryData(commentsCacheKey, (old: any[] = []) => {
				old[request.stepId] = old[request.stepId].filter(
					(x: any) => x.Id !== request.commentId,
				);

				return old;
			});

			return { previousSuggestions };
		},
		onError: (err, id, context) => {
			// Rollback to previous state if an error occurs
			if (context?.previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					context.previousSuggestions,
				);
			}
		},
		onSettled: (response, error, request) => {
			// Ensure that queries are refetched after mutation
			queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
			// Clears the cache for the checklist page
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails],
			});
			queryClient.invalidateQueries({
				queryKey: [
					ChecklistQueryKeys.checklist,
					request.checklistId,
					ChecklistQueryKeys.comments,
				],
			});
		},
	});
};

export const UseDeleteProcedureUpdateSuggestionComment = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) =>
			SATOSPortal.DeleteProcedureUpdateSuggestionComment(id),

		onMutate: async (commentId: number) => {
			await queryClient.cancelQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},

		onSettled: () => {
			// Ensure that queries are refetched after mutation
			queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
	});

	return mutation;
};

export const UseAddProcedureUpdateSuggestionComment = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: any) =>
			SATOSPortal.AddProcedureUpdateSuggestionComment(request),
		onMutate: async (newSuggestion: any) => {
			await queryClient.cancelQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
		onSettled: () => {
			// Refetch the updated list of comments
			queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
	});

	return mutation.mutateAsync;
};

export const UsePutProcedureUpdateSuggestions = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: ({ id, request }) =>
			SATOSPortal.PutProcedureUpdateSuggestion(id, request),
		onMutate: async (updatedSuggestion) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});

			// Snapshot the previous value
			const previousSuggestions = queryClient.getQueryData([
				TaskQueryKeys.procedureUpdatesSuggestions,
			]);

			// Optimistically update to the new value
			if (previousSuggestions && Array.isArray(previousSuggestions)) {
				const updatedSuggestions = previousSuggestions.map((suggestion) =>
					suggestion.id === updatedSuggestion.id
						? { ...suggestion, ...updatedSuggestion.request }
						: suggestion,
				);
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					updatedSuggestions,
				);
			}

			return { previousSuggestions };
		},
		onError: (err, updatedSuggestion, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousSuggestions) {
				queryClient.setQueryData(
					[TaskQueryKeys.procedureUpdatesSuggestions],
					context.previousSuggestions,
				);
			}
		},
		onSettled: () => {
			// Always refetch after error or success:
			queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
			});
		},
	});

	return mutation.mutateAsync;
};

export const UpdateProcedureDetailStepState = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: UpdateProcedureDetailStepStatusRequest) =>
			SATOSPortal.UpdateProcedureDetailStepState(request),
		onMutate: (request) => {
			// Optimistic update status
			const statusesQueryKey = [
				ProcedureQueryKeys.procedureDetails,
				request.ProcedureId,
				ProcedureQueryKeys.details,
				request.SchemeId,
				ProcedureQueryKeys.status,
			];
			queryClient.cancelQueries({ queryKey: statusesQueryKey });

			// Snapshot the previous status state
			const statusesPreviousState =
				queryClient.getQueryData<ProcedureNodeStepStatus[]>(statusesQueryKey);

			// Optimistically update statuses to the new value
			queryClient.setQueryData(statusesQueryKey, (oldData: any) => {
				const stepStatus = oldData.map((x: any) => {
					if (x.Id === oldData.StepId) {
						x.Status = request.Status;
						x.IsSkipped = request.IsSkipped;
						x.IsOverridden = request.IsOverridden;
					}

					return x;
				});

				return stepStatus;
			});

			const checklistQueryKey = [
				ChecklistQueryKeys.checklist,
				request.SchemeId,
				ChecklistQueryKeys.comments,
			];

			let checklistPreviousState: Checklist | undefined;

			if (request.ArchiveType === "Comment") {
				checklistPreviousState =
					queryClient.getQueryData<Checklist>(checklistQueryKey);

				queryClient.setQueryData(
					checklistQueryKey,
					(oldData: Record<number, ProcedureNodeStepComment>) => {
						const newComment = {
							ArchiveType: "Comment",
							IsTask: false,
							Id: 9999999999999999,
							ChecklistId: request.SchemeId,
							StepId: request.StepId,
							ArchiveReference: 0,
							Date: request.TaskDate,
							NumberOfFields: 0,
							Status: 0,
							UserName: request.UserName,
							Value: request.Value,
						};

						if (request?.StepId && Array.isArray(oldData[request?.StepId])) {
							oldData[request.StepId].push(newComment);
						} else {
							oldData[request.StepId] = [newComment];
						}

						return oldData;
					},
				);
			}

			return {
				previousState: { statusesPreviousState, checklistPreviousState },
			};
		},
		// Rollback on error
		onError: (err, request, previousState) => {
			if (previousState) {
				queryClient.setQueryData(
					[
						ProcedureQueryKeys.procedureDetails,
						request.ProcedureId,
						ProcedureQueryKeys.details,
						request.SchemeId,
						ProcedureQueryKeys.status,
					],
					previousState.statusesPreviousState,
				);

				if (request.ArchiveType === "Comment") {
					queryClient.setQueryData(
						[
							ChecklistQueryKeys.checklist,
							request.SchemeId,
							ChecklistQueryKeys.comments,
						],
						previousState.checklistPreviousState,
					);
				}
			}
		},
		// Invalidate and refetch after mutation
		onSuccess: (response, request) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.SchemeId,
					ProcedureQueryKeys.status,
				],
				() => response,
			);
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});

			if (request.ArchiveType === "Comment") {
				queryClient.invalidateQueries({
					queryKey: [
						ChecklistQueryKeys.checklist,
						request.SchemeId,
						ChecklistQueryKeys.comments,
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export const UpdateProcedureDetailsTree = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ProcedureAccordionMove) =>
			SATOSPortal.PostMoveAccordionItem(request),
		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error

		onMutate: (request: ProcedureAccordionMove) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});

			const previousValue: ProcedureDetails | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
				]);

			const newValue = ReorderAccordion(
				previousValue,
				request.Node,
				request.Direction,
			);

			if (newValue) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureDetails, request.ProcedureId],
					newValue,
				);
			}

			return { previousValue, newValue };
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (procedureId) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, procedureId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const SaveKSpicesetting = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: MeasurementPollerConfigurationEndpoint) =>
			SATOSPortal.UpdateKSpicesetting(request),

		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: MeasurementPollerConfigurationEndpoint) => {
			queryClient.cancelQueries({ queryKey: ["kspice", request.DbId] });
			const previousValue: MeasurementPollerConfigurationEndpoint | undefined =
				queryClient.getQueryData(["kspice", request.DbId]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(["kspice", variables.DbId], previousValue);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						"kspice",
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const useMutateDeleteProcedure = () => {
	const queryClient = useQueryClient();
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;

	const mutation = useMutation({
		mutationFn: (request: EditProcedure) =>
			SATOSPortal.DeleteProcedure({ procedureId: request.ProcedureId }),

		onMutate: (request: EditProcedure) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureGroup, request.ProjectGroup],
			});

			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});
			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedures,
				ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						"procedure",
						variables.procedureId,
						{ refetchActive: true, refetchInactive: true },
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.groupdetails,
						variables.ParentId,
						companyId,
					],
				});
			}

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.groupdetails],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedure],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useMutateUpdateProcedureSettings = () => {
	const queryClient = useQueryClient();

	const user = useContext(UserContext);
	const { mutateAsync: updateProcedure, isPending: isSubmitting } = useMutation(
		{
			mutationFn: (request: EditProcedure) =>
				SATOSPortal.UpdateProcedureSetting(request),

			// Optimistically update the cache value on mutate, but store
			// the old value and return it so that it's accessible in case of
			// an error
			onMutate: (request: EditProcedure) => {
				queryClient.cancelQueries({
					queryKey: [ProcedureQueryKeys.procedure, request.ProcedureId],
				});
				const previousValue: EditProcedure | undefined =
					queryClient.getQueryData([
						ProcedureQueryKeys.procedure,
						request.ProcedureId,
					]);

				return previousValue;
			},
			// On failure, roll back to the previous value
			onError: (err, variables, previousValue) => {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedure, variables.ProcedureId],
					previousValue,
				);

				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedure, variables.ProcedureId],
				});
			},
			// After success or failure, refetch the todos query
			onSettled: (data, error, variables: EditProcedure) => {
				if (variables) {
					queryClient.invalidateQueries({
						queryKey: [ProcedureQueryKeys.procedure, variables.ProcedureId],
					});
				}
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedures],
				});

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});

				// Invalidates the cache for the checklist page
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
			},
		},
	);
	return { updateProcedure, isSubmitting };
};

export const MergeFileLinks = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: number) => SATOSPortal.MergeFileLinks(request),

		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: number) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.fileMerge, request],
			});
			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([ProcedureQueryKeys.fileMerge, request]);
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.fileMerge, variables],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.fileMerge,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const CreateNewProcedureUpdateNotice = () => {
	const queryClient = useQueryClient();
	const { id } = useParams();

	const mutation = useMutation({
		mutationFn: (request: ProcedureUpdateNoticeRequest) => {
			return SATOSPortal.PostProcedureUpdateNotice(request);
		},
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, request.ProcedureId],
			});

			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedureKnowledge,
				request.ProcedureId,
			]);

			return { previousValues };
		},
		onError: (err, variables, context) => {
			if (context?.previousValues) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureKnowledge, variables.ProcedureId],
					context.previousValues,
				);
			}
		},
		onSettled: (variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, id],
			});
		},
	});

	return mutation.mutateAsync;
};
export const useMutateProcedureKnowledgeStatus = () => {
	const queryClient = useQueryClient();
	const { id } = useParams();

	const mutation = useMutation({
		mutationFn: (request: CompetenceStatusUpdate) => {
			return SATOSPortal.PostProcedureKnowledgeStatus(request);
		},
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, request.ProcedureId],
			});

			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedureKnowledge,
				request.ProcedureId,
			]);

			return { previousValues };
		},
		onError: (err, variables, context) => {
			if (context?.previousValues) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureKnowledge, variables.ProcedureId],
					context.previousValues,
				);
			}
		},
		onSettled: (variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureKnowledge, id],
			});
		},
	});

	return mutation.mutateAsync;
};

export const useCreateChecklist = () => {
	const queryClient = useQueryClient();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;
	const { mutateAsync } = useMutation({
		mutationFn: (request: CreateChecklistRequest) =>
			SATOSPortalNew.CreateChecklist(request),

		onMutate: () => {
			queryClient.cancelQueries({ queryKey: [ProcedureQueryKeys.procedures] });
			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedures,
			]);

			return { previousValues };
		},
		onError: (err, _, context) => {
			if (context?.previousValues) {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedures],
					context.previousValues,
				);
			}
		},
		onSettled: (variables) => {
			queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, +variables?.ChecklistId],
			});
		},
	});

	return mutateAsync;
};

export const UpsertProcedureNodeSteps = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: QueryUpsertProcedureNodeSteps) =>
			SATOSPortal.QueryUpsertProcedureNodeSteps(request),
		onMutate: (request: QueryUpsertProcedureNodeSteps) => {
			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.ChecklistId,
				],
			});
			queryClient.cancelQueries({
				queryKey: [ChecklistQueryKeys.checklist, request.ChecklistId],
			});

			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.ChecklistId,
				]);

			const mergedSteps = previousValue?.Steps.map((step) => {
				return {
					...step,
					...request.NodeSteps.find((mut) => mut.Id === step.Id),
				};
			});

			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.ChecklistId,
				],
				{
					...previousValue,
					Name: request.Name,
					Ingress: request.Ingress,
					Steps: mergedSteps,
				},
			);

			return previousValue;
		},
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.procedureDetails,
					variables.ProcedureId,
					ProcedureQueryKeys.details,
					variables.ChecklistId,
				],
				previousValue,
			);
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
					refetchActive: true,
					refetchInactive: true,
				});
				queryClient.invalidateQueries({
					queryKey: [ChecklistQueryKeys.checklist, variables.ChecklistId],
				});
			}
		},
		onSuccess: (data, variables, previousValues) => {
			const merged: ProcedureNodeSteps = {
				...data,
				Steps: data.NodeSteps,
				ActiveListenerTags: previousValues?.ActiveListenerTags ?? [],
				ListenerTags: previousValues?.ListenerTags ?? [],
				IsModified: false,
			};

			queryClient.setQueryData<ProcedureNodeSteps>(
				[
					ProcedureQueryKeys.procedureDetails,
					variables.ProcedureId,
					ProcedureQueryKeys.details,
					variables.ChecklistId,
				],
				merged,
			);
		},
	});

	return mutation.mutateAsync;
};

export const useDeleteChecklist = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DeleteProcedureChecklistRequest) =>
			SATOSPortal.DeleteProcedureChecklist(request),
		onMutate: (request: DeleteProcedureChecklistRequest) => {
			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
					ProcedureQueryKeys.details,
					request.SchemeId,
				],
			});
			const previousValue: ProcedureNodeSteps | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					request.ProcedureId,
				]);

			return previousValue;
		},
	});

	return mutation.mutateAsync;
};

export const useConvertToManualChecklist = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ConvetToManualChecklistRequest) =>
			SATOSPortal.ConvertToManualChecklist(request),
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
					refetchActive: true,
					refetchInactive: true,
				});
			}
		},
	});

	return mutation.mutateAsync;
};

// TODO: Ustin - Bad mutation, needs onMutate and onError
export const DeleteProcedureNode = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DeleteProcedureNodeRequest) =>
			SATOSPortal.DeleteProcedureNode(request),
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						+variables.ProcedureId,
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

// TODO: Ustin - Bad mutation, needs onMutate and onError
export const UpdateProcedureVersion = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UppdatedProcedureRequest) =>
			SATOSPortal.UpdateProcedureVersion(request),
		onSettled: (data, err, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.document],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.checklist, variables.checklistId],
			});
		},
	});

	return mutation.mutateAsync;
};

export const UppdatedProcedureCheckList = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: number | undefined) =>
			SATOSPortal.UppdatedProcedureCheckList(request),
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.updatechecklist, variables],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SaveAndResetProcedure = () => {
	const queryClient = useQueryClient();

	const { id, schemeId } = useParams<RouteParams>();

	const mutation = useMutation({
		mutationFn: (request: SaveProcedureRequest) =>
			SATOSPortal.SaveAndResetProcedure(request),
		onMutate: (request: SaveProcedureRequest) => {
			const { ProcedureId } = request;
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, ProcedureId],
			});
			const previousValues: ProcedureDetails | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureDetails,
					ProcedureId,
				]);

			return previousValues;
		},
		onError: (err, variables, previousValues) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
				previousValues,
			);
		},
		onSettled: (variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails],
			});
			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureDetails,
					id,
					ProcedureQueryKeys.details,
					schemeId,
					ProcedureQueryKeys.status,
				],
			});
		},
	});
	return mutation.mutateAsync;
};

export const ImportPlanFromWizard = () => {
	const queryClient = useQueryClient();
	const user = useContext(UserContext);
	const mutation = useMutation({
		mutationFn: (request: DocumentImportPlan | undefined) =>
			SATOSPortal.ImportPlanFromWizard(request),
		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: DocumentImportPlan | undefined) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.importPlanFromWizard, request],
			});
			const previousValue: DocumentImportPlan | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.importPlanFromWizard,
					request,
				]);
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.importPlanFromWizard, variables],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.importPlanFromWizard,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const ReimportDocument = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DocumentUpdateRequest | undefined) =>
			SATOSPortal.ReimportDocument(request),
		onSettled: (data, error, variables) => {
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.document],
			});
			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables?.ProcedureId],
			});
		},
	});

	return mutation.mutateAsync;
};

// TODO: Ustin - Bad mutation, needs onMutate, onError and onSettled
export const useMutateUpdateSharepointDocuments = () => {
	const mutation = useMutation({
		mutationFn: () => SATOSPortal.UpdateSharepointDocuments(),
	});

	return mutation.mutateAsync;
};

export const UseUpdateProcedureGroup = () => {
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;
	const queryClient = useQueryClient();

	const { mutateAsync: updateProcedureFolder, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: ProcedureGroup) =>
				SATOSPortal.UpdateProcedureFolder(request),
			onMutate: (request: ProcedureGroup) => {
				queryClient.cancelQueries({
					queryKey: [ProcedureQueryKeys.procedureGroup, request.Id],
				});
				queryClient.setQueryData(
					[ProcedureQueryKeys.groupdetails, request.Id, companyId],
					request,
				);
			},
			onError: (err, variables, previousValue) => {
				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureGroup, variables.Id],
					previousValue,
				);
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureGroups,
						companyId,
						ProcedureQueryKeys.orderByNameAsc,
					],
				});
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureGroups,
						companyId,
						ProcedureQueryKeys.orderByNameAsc,
					],
				});

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});

				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.metaData],
				});
			},
		});

	return { updateProcedureFolder, isSubmitting };
};

export const SaveProcedureGroup = () => {
	const user = useContext(UserContext);
	const companyId = user?.CompanyId;

	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ProcedureGroup) =>
			SATOSPortal.UpdateProcedureGroup(request),
		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: ProcedureGroup) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureGroup, request.Id],
			});
			const previousValue: ProcedureGroup | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.procedureGroup,
					request.Id,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureGroup, variables.Id],
				previousValue,
			);

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureGroup,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedureGroups,
					companyId,
					ProcedureQueryKeys.orderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				],
			});

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.metaData],
			});
		},
	});
	return mutation.mutateAsync;
};

export const UpdateProcedureNode = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UpdateProcedureNodeRequest) =>
			SATOSPortal.UpdateProcedureNode(request),
		onMutate: (request: UpdateProcedureNodeRequest) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, request.ProcedureId],
			});
			const previousValues = queryClient.getQueryData([
				ProcedureQueryKeys.procedureDetails,
				request.ProcedureId,
			]);

			return previousValues;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.document,
						ProcedureQueryKeys.node,
						variables.SchemeId,
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const UpdateProcedureNodeDocument = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: ImportProcedureNodeDocumentRequest) =>
			SATOSPortal.UpdateProcedureNodeDocument(request),
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.document,
						ProcedureQueryKeys.node,
						variables.SchemeId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.document],
				});
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureDetails],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

// TODO: Ustin - Bad mutation, needs onMutate and onError
export const RefreshSharePointDocument = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (request: RefreshSharePointDocumentRequest) =>
			SATOSPortal.RefreshSharePointDocument(request),
		onSettled: (variables) => {
			if (variables?.Success) {
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.document],
				});
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.details],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export const UploadProcedureNodeDocument = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: UploadProcedureNodeDocumentRequest) =>
			SATOSPortal.UploadProcedureNodeDocument(request),
		onMutate: (request) => {
			return request;
		},
		onError: (_, variables, previousValues) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.document,
					ProcedureQueryKeys.node,
					variables.SchemeId,
				],
				previousValues,
			);

			queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, variables.ProcedureId],
			});
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						[
							ProcedureQueryKeys.document,
							ProcedureQueryKeys.node,
							variables.SchemeId.toString(),
						],
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export const CreateNewProcedureNode = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: CreateNewProcedureNodeRequest) =>
			SATOSPortal.CreateNewProcedureNode(request),
		onMutate: (request) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.document, ProcedureQueryKeys.node],
			});

			return request;
		},
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						[ProcedureQueryKeys.document, ProcedureQueryKeys.node],
						{ refetchActive: true, refetchInactive: true },
					],
				});

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedureDetails,
						variables.ProcedureId,
					],
				});
			}
		},
	});

	return mutation.mutateAsync;
};

export const SaveNodeDiagramTemplate = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: NodeDiagramTemplate) =>
			SATOSPortal.SaveNodeDiagramTemplate(request),
		// Optimistically update the cache value on mutate, but store
		// the old value and return it so that it's accessible in case of
		// an error
		onMutate: (request: NodeDiagramTemplate) => {
			queryClient.cancelQueries({
				queryKey: [
					ProcedureQueryKeys.NodeDiagramTemplate,
					request.NodeDiagramTemplateId,
				],
			});
			const previousValue: NodeDiagramTemplate | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.NodeDiagramTemplate,
					request.NodeDiagramTemplateId,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.NodeDiagramTemplate,
					variables.NodeDiagramTemplateId,
				],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.NodeDiagramTemplate, variables],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SetTemplateForProcedure = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: NodeDiagramTemplateReturn) =>
			SATOSPortal.SetTemplateForProcedure(request),
		onMutate: (request: NodeDiagramTemplateReturn) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.templateforprocedure, request],
			});
			const previousValue: NodeDiagramTemplateReturn | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.templateforprocedure,
					request,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					ProcedureQueryKeys.templateforprocedure,
					variables.NodeDiagramTemplateId,
				],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.templateforprocedure,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const UpdateDiagramNodes = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ProcedureNodeDiagramSave) =>
			SATOSPortal.UpdateDiagramNodes(request),
		onMutate: (request: ProcedureNodeDiagramSave) => {
			queryClient.cancelQueries({
				queryKey: [ProcedureQueryKeys.updatediagramnodes, request],
			});
			const previousValue: NodeDiagramTemplate | undefined =
				queryClient.getQueryData([
					ProcedureQueryKeys.updatediagramnodes,
					request,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[ProcedureQueryKeys.updatediagramnodes, variables],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.updatediagramnodes, variables],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const useMutateCreateNewProcedure = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createProcedure, isPending: isSubmitting } = useMutation(
		{
			mutationFn: (request: CreateNewProcedureRequest) =>
				SATOSPortal.CreateProcedure(request),
			onMutate: () => {
				queryClient.cancelQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});
				const previousValues = queryClient.getQueryData([
					ProcedureQueryKeys.procedures,
					ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
				]);

				return previousValues;
			},
			onError: (_, variables, previousValues) => {
				queryClient.setQueryData(
					[
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
					previousValues,
				);

				queryClient.invalidateQueries({
					queryKey: [
						ProcedureQueryKeys.procedures,
						ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
					],
				});
			},
			onSettled: (variables) => {
				if (variables) {
					queryClient.invalidateQueries({
						queryKey: [
							ProcedureQueryKeys.procedures,
							ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc,
						],
					});
				}
			},
		},
	);

	return { createProcedure, isSubmitting };
};
